import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Logout } from "@mui/icons-material";

import CustomDialog from "../../../../components/modals/CustomDialog";
import { useAppDispatch } from "../../../../app/hooks";
import {
  forceLogoutAllUsers,
  forceLogoutUser,
} from "../../setupSlices";

export type ForceLogoutTarget = {
  id: string;
  name?: string;
  email?: string;
};

type Props = {
  open: boolean;
  onClose: (success: boolean) => void;
  /** One or more specific users. */
  targets?: ForceLogoutTarget[];
  /** Sign out every user in scope (global or company). */
  allUsers?: boolean;
  allScope?: "global" | "company";
};

type Phase = "confirm" | "running" | "done";

type Failure = {
  label: string;
  error: string;
};

const targetLabel = (t: ForceLogoutTarget) =>
  t.name?.trim() || t.email?.trim() || t.id;

export function ForceLogoutDialog({
  open,
  onClose,
  targets = [],
  allUsers = false,
  allScope = "company",
}: Props) {
  const dispatch = useAppDispatch();
  const [phase, setPhase] = useState<Phase>("confirm");
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [successMessage, setSuccessMessage] = useState("");
  const [failures, setFailures] = useState<Failure[]>([]);

  useEffect(() => {
    if (open) {
      setPhase("confirm");
      setProgress({ done: 0, total: 0, current: "" });
      setSuccessMessage("");
      setFailures([]);
    }
  }, [open, targets, allUsers, allScope]);

  const handleClose = (success: boolean) => {
    onClose(success);
  };

  const runSelected = async () => {
    const list = targets.filter((t) => t.id);
    if (!list.length) return;

    setPhase("running");
    setProgress({ done: 0, total: list.length, current: targetLabel(list[0]) });

    const failed: Failure[] = [];
    let succeeded = 0;

    for (let i = 0; i < list.length; i++) {
      const target = list[i];
      setProgress({
        done: i,
        total: list.length,
        current: targetLabel(target),
      });
      const result = await dispatch(forceLogoutUser(target.id));
      if (forceLogoutUser.fulfilled.match(result)) {
        succeeded += 1;
      } else {
        const message =
          (result.payload as { message?: string } | undefined)?.message ??
          result.error?.message ??
          "Force logout failed";
        failed.push({ label: targetLabel(target), error: message });
      }
    }

    setProgress({ done: list.length, total: list.length, current: "" });
    setFailures(failed);
    if (succeeded > 0) {
      if (list.length === 1 && failed.length === 0) {
        setSuccessMessage(
          `${targetLabel(list[0])} was signed out of all sessions.`,
        );
      } else if (failed.length === 0) {
        setSuccessMessage(
          `${succeeded} user${succeeded === 1 ? "" : "s"} signed out of all sessions.`,
        );
      } else {
        setSuccessMessage(
          `${succeeded} of ${list.length} users signed out successfully.`,
        );
      }
    }
    setPhase("done");
  };

  const runAll = async () => {
    setPhase("running");
    setProgress({ done: 0, total: 1, current: "Signing out users…" });

    const result = await dispatch(forceLogoutAllUsers());
    setProgress({ done: 1, total: 1, current: "" });

    if (forceLogoutAllUsers.fulfilled.match(result)) {
      const { scope, signedOutCount } = result.payload;
      if (scope === "global") {
        setSuccessMessage(
          "All users have been signed out system-wide. They can log back in immediately.",
        );
      } else if (signedOutCount === 0) {
        setSuccessMessage("No users were found in your company scope.");
      } else {
        setSuccessMessage(
          `${signedOutCount} user${signedOutCount === 1 ? "" : "s"} signed out of all sessions.`,
        );
      }
      setPhase("done");
      return;
    }

    const message =
      (result.payload as { message?: string } | undefined)?.message ??
      result.error?.message ??
      "Force logout failed";
    setFailures([{ label: "Force logout all", error: message }]);
    setPhase("done");
  };

  const handleConfirm = () => {
    if (allUsers) {
      void runAll();
    } else {
      void runSelected();
    }
  };

  const confirmTitle = allUsers
    ? "Force logout all users"
    : targets.length === 1
      ? "Force logout user"
      : `Force logout ${targets.length} users`;

  const confirmBody = allUsers
    ? allScope === "global"
      ? "Sign out EVERY user from all devices? They will have to log in again."
      : "Sign out all users in your company (and partner companies) from all devices? They will have to log back in."
    : targets.length === 1
      ? `Sign ${targetLabel(targets[0])} out of all devices now?`
      : `Sign ${targets.length} users out of all devices now?`;

  const progressPct =
    progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const overallSuccess =
    phase === "done" && (successMessage.length > 0 || failures.length === 0);

  return (
    <CustomDialog
      title={confirmTitle}
      isOpen={open}
      handleDialogClose={() => handleClose(overallSuccess)}
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 2 }}>
        {phase === "confirm" && (
          <Stack spacing={2}>
            <Typography variant="body2">{confirmBody}</Typography>
            {!allUsers && targets.length > 1 && (
              <Box
                sx={{
                  maxHeight: 160,
                  overflowY: "auto",
                  p: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "action.hover",
                }}
              >
                {targets.map((t, i) => (
                  <Typography key={t.id} variant="caption" component="div" noWrap>
                    {`${i + 1}. ${targetLabel(t)}`}
                  </Typography>
                ))}
              </Box>
            )}
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button onClick={() => handleClose(false)}>Cancel</Button>
              <Button
                color="warning"
                variant="contained"
                startIcon={<Logout />}
                onClick={handleConfirm}
              >
                Sign out
              </Button>
            </Stack>
          </Stack>
        )}

        {phase === "running" && (
          <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress color="warning" />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {allUsers
                ? "Signing out users…"
                : targets.length > 1
                  ? `Signing out ${progress.done + 1} of ${progress.total}: ${progress.current}`
                  : `Signing out ${progress.current}…`}
            </Typography>
            {targets.length > 1 && !allUsers && (
              <LinearProgress
                variant="determinate"
                value={progressPct}
                color="warning"
                sx={{ width: "100%" }}
              />
            )}
          </Stack>
        )}

        {phase === "done" && (
          <Stack spacing={2}>
            {successMessage && (
              <Alert severity={failures.length ? "warning" : "success"}>
                {successMessage}
              </Alert>
            )}
            {failures.map((f) => (
              <Alert key={`${f.label}-${f.error}`} severity="error">
                <Typography variant="body2" fontWeight={600}>
                  {f.label}
                </Typography>
                <Typography variant="body2">{f.error}</Typography>
              </Alert>
            ))}
            {!successMessage && failures.length === 0 && (
              <Alert severity="info">No users were signed out.</Alert>
            )}
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={() => handleClose(overallSuccess)}>
                Close
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </CustomDialog>
  );
}
